import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "leaflet/dist/leaflet.css"; // Leaflet CSS - loaded locally to avoid CDN blocking
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { analytics } from "./utils/analytics";

// Capture admin deep-link query params at the very first render so they survive
// any login redirect (which strips ?tab=...&batch=...&story=...). Consumed by
// AdminDashboard, BatchManager, and the Stories tab on mount.
(() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const batch = params.get('batch');
    const story = params.get('story');
    if (tab || batch || story) {
      const payload = { tab, batch, story, ts: Date.now() };
      sessionStorage.setItem('admin_deeplink', JSON.stringify(payload));
    }
  } catch (_) { /* ignore */ }
})();

// --- Eagerly-loaded shell + homepage (needed for first paint) ---
import { AuthProvider } from "./components/UserAuth";
import { ThemeProvider } from "./contexts/ThemeContext";
import { MapFilterProvider } from "./contexts/MapFilterContext";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { InteractiveMap } from "./components/InteractiveMap";
import { FeaturedStories } from "./components/FeaturedStories";
import { CTA } from "./components/CTA";
import { Footer } from "./components/Footer";
import { ClubShowcase } from "./components/ClubShowcase";
// Club pages referenced from Home's domain-based router (kept eager to avoid
// a flash on club domains).
import { TravelRockClubPage } from "./components/TravelRockClubPage";
import CuriKittenClubPage from "./components/CuriKittenClubPage";
import GuestbookClubPage from "./components/GuestbookClubPage";
import KindnessClubPage from "./components/KindnessClubPage";

// --- Lazy-loaded routes (code-split into separate chunks) ---
// Default-export components
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const BulkItemCreation = lazy(() => import("./components/BulkItemCreation"));
const Storefront = lazy(() => import("./components/Storefront"));
const ColoringPageStorefront = lazy(() => import("./components/ColoringPageStorefront"));
const PasswordResetPage = lazy(() => import("./components/PasswordResetPage"));
const PasswordSetupPage = lazy(() => import("./components/PasswordSetupPage"));
const AccountUpgrade = lazy(() => import("./components/AccountUpgrade"));
const ItemVerificationPage = lazy(() => import("./components/ItemVerificationPage"));
const SubscriptionSuccess = lazy(() => import("./components/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("./components/SubscriptionCancel"));
const BulkQRRegister = lazy(() => import("./components/BulkQRRegister"));
const QRScanHandler = lazy(() => import("./components/QRScanHandler"));
const TestRoute = lazy(() => import("./components/TestRoute"));
const MagicLinkVerification = lazy(() => import("./components/MagicLinkVerification"));
const EmailVerificationPage = lazy(() => import("./components/EmailVerificationPage"));
const VerifyStory = lazy(() => import("./components/VerifyStory"));
const CheckoutPage = lazy(() => import("./components/CheckoutPage"));
const CartPage = lazy(() => import("./components/CartPage"));

// Named-export components — re-shape to default export for React.lazy
const CreateItemPage = lazy(() =>
  import("./components/CreateItemPage").then((m) => ({ default: m.CreateItemPage }))
);
const AllStoriesPage = lazy(() =>
  import("./components/AllStoriesPage").then((m) => ({ default: m.AllStoriesPage }))
);
const SubscriptionPage = lazy(() =>
  import("./components/SubscriptionPage").then((m) => ({ default: m.SubscriptionPage }))
);
const AdminLogin = lazy(() =>
  import("./components/AdminLogin").then((m) => ({ default: m.AdminLogin }))
);
const ItemDetailPage = lazy(() =>
  import("./components/ItemDetailPage").then((m) => ({ default: m.ItemDetailPage }))
);
const UserNotificationSettings = lazy(() =>
  import("./components/UserNotificationSettings").then((m) => ({ default: m.UserNotificationSettings }))
);
const CreateLocationPage = lazy(() =>
  import("./components/CreateLocationPage").then((m) => ({ default: m.CreateLocationPage }))
);
const LeaderboardPage = lazy(() =>
  import("./components/LeaderboardPage").then((m) => ({ default: m.LeaderboardPage }))
);
const BrandPreview = lazy(() =>
  import("./components/BrandPreview").then((m) => ({ default: m.BrandPreview }))
);
const BrandPreviewBlended = lazy(() =>
  import("./components/BrandPreviewBlended").then((m) => ({ default: m.BrandPreviewBlended }))
);
const FreeGuestbookPoster = lazy(() =>
  import("./pages/FreeGuestbookPoster").then((m) => ({ default: m.FreeGuestbookPoster }))
);
const FreeLabelStickers = lazy(() =>
  import("./pages/FreeLabelStickers").then((m) => ({ default: m.FreeLabelStickers }))
);
const FreeSquareLabels = lazy(() =>
  import("./pages/FreeSquareLabels").then((m) => ({ default: m.FreeSquareLabels }))
);
const ClubPage = lazy(() =>
  import("./components/ClubPage").then((m) => ({ default: m.ClubPage }))
);

// Lightweight fallback shown while a route chunk loads.
const RouteFallback = () => (
  <div
    data-testid="route-loading-fallback"
    className="min-h-screen flex items-center justify-center bg-slate-100"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="h-10 w-10 rounded-full border-4 border-emerald-200 border-t-emerald-600 animate-spin" />
      <p className="text-sm text-slate-500">Loading…</p>
    </div>
  </div>
);

// Page view tracking component
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    analytics.trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Homepage component that handles domain-based routing
const Home = () => {
  // Check if we're on a club domain and redirect to appropriate club page
  if (typeof window !== 'undefined') {
    const domain = window.location.hostname.toLowerCase();
    if (domain === 'travelrock.club' || domain === 'www.travelrock.club') {
      return <TravelRockClubPage />;
    }
    if (domain === 'curithekitten.com' || domain === 'www.curithekitten.com') {
      return <CuriKittenClubPage />;
    }
    if (domain === 'iwashere.today' || domain === 'www.iwashere.today') {
      return <GuestbookClubPage />;
    }
    if (
      domain === 'kindnessproject.app' || domain === 'www.kindnessproject.app' ||
      domain === 'paykindnessforward.club' || domain === 'www.paykindnessforward.club'
    ) {
      return <KindnessClubPage />;
    }
  }

  return (
    <MapFilterProvider>
      <div className="min-h-screen bg-slate-100">
        <Header />
        <Hero />
        {/* Subtle transition from dark hero to light background */}
        <div className="h-20 bg-gradient-to-b from-slate-900 to-slate-100"></div>
        <div id="map" className="bg-slate-100">
          <InteractiveMap />
        </div>
        <div id="stories" className="bg-slate-50">
          <FeaturedStories />
        </div>
        <div className="bg-white">
          <ClubShowcase />
        </div>
        <div id="how-it-works" className="bg-stone-50">
          <CTA />
        </div>
        <Footer />
      </div>
    </MapFilterProvider>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <ScrollToTop />
          <PageViewTracker />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/create-item" element={<CreateItemPage />} />
            <Route path="/bulk-create" element={<BulkItemCreation />} />
            <Route path="/bulk-register" element={<BulkQRRegister />} />
            <Route path="/test" element={<TestRoute />} />
            <Route path="/qr/:uid" element={<QRScanHandler />} />
            <Route path="/qr/:uid/:mode" element={<QRScanHandler />} />
            <Route path="/verify-story" element={<VerifyStory />} />
            <Route path="/all-stories" element={<AllStoriesPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/store" element={<Storefront />} />
            <Route path="/store/coloring-pages" element={<ColoringPageStorefront />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/reset-password" element={<PasswordResetPage />} />
            <Route path="/setup-password" element={<PasswordSetupPage />} />
            <Route path="/upgrade-account" element={<AccountUpgrade />} />
            <Route path="/verify-item" element={<ItemVerificationPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
            <Route path="/user/notifications" element={<UserNotificationSettings />} />
            <Route path="/auth/magic-login" element={<MagicLinkVerification />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/create-location" element={<CreateLocationPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/brand-preview" element={<BrandPreview />} />
            <Route path="/brand-preview-blended" element={<BrandPreviewBlended />} />
            <Route path="/free/guestbook-poster" element={<FreeGuestbookPoster />} />
            <Route path="/free/label-stickers" element={<FreeLabelStickers />} />
            <Route path="/free/square-labels" element={<FreeSquareLabels />} />
            {/* Club Pages - White-label landing pages for specific communities */}
            <Route path="/club/jeepduck" element={<ClubPage />} />
            <Route path="/club/travelrock" element={<TravelRockClubPage />} />
            <Route path="/club/curi" element={<CuriKittenClubPage />} />
            <Route path="/club/guestbook" element={<GuestbookClubPage />} />
            <Route path="/club/kindness" element={<KindnessClubPage />} />
            {/* Item detail route - shows item journey and details */}
            <Route path="/item/:uid" element={<ItemDetailPage />} />
            {/* Catch-all route - redirect any unmatched paths to homepage */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
